import { 
  CpuChipIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import modelsData from '../data/models.json';

interface ModelDetails {
  id: string;
  name: string;
  provider: string;
}

interface ModelsResponse {
  data: ModelDetails[];
}

const getProviderIcon = (provider: string) => {
  switch (provider) {
    case 'google':
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    case 'meta-llama':
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
          <path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
        </svg>
      );
    case 'qwen':
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14z"/>
          <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5z"/>
        </svg>
      );
    case 'openchat':
      return (
        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
        </svg>
      );
    default:
      return <CpuChipIcon className="h-5 w-5" />;
  }
};

export default function Models() {
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const models = modelsData as ModelsResponse;

  const copyToClipboard = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const providers = ['all', ...new Set(models.data.map(model => model.provider))];

  const filteredModels = selectedProvider === 'all' 
    ? models.data 
    : models.data.filter(model => model.provider === selectedProvider);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <CpuChipIcon className="h-6 w-6 text-white/90" />
            <h1 className="text-xl font-medium text-white">Model Zoo</h1>
          </div>
          
          {/* Provider Filter */}
          <div className="flex items-center space-x-2">
            {providers.map(provider => (
              <button
                key={provider}
                onClick={() => setSelectedProvider(provider)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  selectedProvider === provider
                    ? 'bg-magick-purple text-white'
                    : 'text-white/60 hover:text-white hover:bg-white/[0.05]'
                }`}
              >
                {provider === 'all' ? 'All' : provider.split('-').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Model Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredModels.map((model) => (
          <div key={model.name} className="glass-panel group">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-white/[0.05] group-hover:bg-white/[0.08] transition-colors duration-300">
                    {getProviderIcon(model.provider)}
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{model.name}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="text-xs px-2 py-1 rounded-full bg-white/[0.05] text-white/70">
                        {model.provider.split('-').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(model.id)}
                  className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.08] transition-colors duration-200"
                  title="Copy model ID"
                >
                  {copiedId === model.id ? (
                    <CheckIcon className="h-5 w-5 text-emerald-400" />
                  ) : (
                    <ClipboardDocumentIcon className="h-5 w-5 text-white/70" />
                  )}
                </button>
              </div>
              <div className="mt-3">
                <div className="text-sm text-white/50 font-mono break-all">
                  {model.id}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
